import { useParams } from 'react-router-dom'
import { Battery } from '../../../interfaces/battery'
import { Price } from '../../../interfaces/price'
import { Priority } from '../../../interfaces/priority'
import { Hardware } from '../../../interfaces/hardware'
import { PvInstallation } from '../../../interfaces/pvinstallation'
import { InputData } from '../../../interfaces/input_data'
import { HeatPumpSource } from '../../../interfaces/heatpumpsource'
import { HeatPump } from '../../../interfaces/heatpump'
import { ElDemand } from '../../../interfaces/eldemand'
import { HeatDemand } from '../../../interfaces/heatdemand'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import axios from 'axios'
import DialogActions from '@mui/material/DialogActions'
import BatteryList from '../../battery/BatteryList'
import PriceList from '../../price/PriceList'
import PriorityList from '../../priority/PriorityList'
import HardwareList from '../../hardware/HardwareList'
import InputDataList from '../../input_data/InputDataList'
import PvInstallationList from '../../pvinstallation/PvInstallationList'
import HeatPumpList from '../../heatpump/HeatPumpList'
import HeatPumpSourceList from '../../heatpumpsource/HeatPumpSourceList'
import React from 'react'
import StartIcon from '@mui/icons-material/PlayArrow'
import Typography from '@mui/material/Typography'
import ButtonDialog from '../../shared/ButtonDialog'
import ElDemandList from '../../eldemand/ElDemandList'
import HeatDemandList from '../../heatdemand/HeatDemandList'
import { Box } from '@mui/material'
import { Check, Clear } from '@mui/icons-material'

interface RequiredPropertyProps {
	label: string
	check: boolean
}

const RequiredProperty = ({ label, check }: RequiredPropertyProps) => {
	return (
		<Box display='flex' alignItems='center' gap={1}>
			{check ? <Check fontSize='small' color='success' /> : <Clear fontSize='small' color='error' />}
			{label}
		</Box>
	)
}

interface Props {
	visible: boolean
	simulation: any
	setSimulation: any
}

export default function Config({ visible, simulation, setSimulation }: Props) {
	const { name } = useParams()
	const [error, setError] = React.useState<any>(undefined)
	const [open, setOpen] = React.useState<boolean>(false)

	const priceInputData = simulation?.simulation_input_data?.price_simulation?.data_file
	const weatherInputData = simulation?.simulation_input_data?.weather_simulation?.data_file
	const pvinstallationInputData = simulation?.simulation_input_data?.pv_production_simulation?.data_file
	const electricityDemandData = simulation?.simulation_input_data?.electricity_demand_simulation?.data_file
	const price = simulation?.price && Object.keys(simulation.price).length > 0
	const priority = simulation?.priority && Object.keys(simulation.priority).length > 0
	const electricityDemand = simulation?.eldemand && Object.keys(simulation.eldemand).length > 0

	const checkRunSimulation = () => {
		if (!priceInputData) return false
		if (!pvinstallationInputData && !weatherInputData) return false
		if (!electricityDemandData) return false
		if (!price) return false
		if (!priority) return false
		if (!electricityDemand) return false
		return true
	}

	const getDialogContent = () => {
		return checkRunSimulation() ? (
			<>
				<Typography variant='body2'>
					Are you sure you want to run this simulation? Depending on the time duration, this could take a while.
				</Typography>
				<DialogActions sx={{ p: 0, pt: 2 }}>
					<Button onClick={() => setOpen(false)}>Cancel</Button>
					<Button
						onClick={async (e) => {
							try {
								setOpen(false)
								setError(undefined)
								simulation.status = { running: true, progress: 0.0 }
								setSimulation({ ...simulation })
								await axios.get(`${process.env.REACT_APP_API}/${name}/run`, { withCredentials: true })
							} catch (err: any) {
								const error = err
								if (error.response.data.error) setError(error.response.data.error)
								simulation.status = { running: false, progress: 0.0, error: error.response.data._error }
								setSimulation({ ...simulation })
							}
						}}
					>
						Run
					</Button>
				</DialogActions>
			</>
		) : (
			<>
				<Typography variant='body2'>
					In order to run a simulation, you need to configure at least following properties:
				</Typography>
				<Box sx={{ '& .MuiBox-root': { mt: 0 } }}>
					<RequiredProperty label={'Price input data'} check={priceInputData} />
					<RequiredProperty label={'Weather or PV installation input data'} check={weatherInputData || pvinstallationInputData} />
					<RequiredProperty label={'Electricity Demand input data'} check={electricityDemandData} />
					<RequiredProperty label={'Price'} check={price} />
					<RequiredProperty label={'Priority'} check={priority} />
					<RequiredProperty label={'Electricity Demand'} check={electricityDemand} />
				</Box>
				<DialogActions sx={{ p: 0, pt: 2 }}>
					<Button onClick={() => setOpen(false)}>Ok</Button>
				</DialogActions>
			</>
		)
	}

	return (
		<Stack
			sx={{
				display: visible ? 'flex' : 'none'
			}}
		>
			<ButtonDialog
				disabled={simulation?.status?.running}
				title={`Run Simulation`}
				icon={<StartIcon />}
				content={getDialogContent()}
				open={open}
				setOpen={setOpen}
			/>
			<Typography variant='caption' color='error' sx={{ mx: 2, mt: -1 }}>
				{error || simulation?.status?.error}
			</Typography>
			<InputDataList
				disabled={simulation?.status?.running}
				type={'simulation_input_data'}
				data={
					Object.entries(simulation?.simulation_input_data || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as InputData[]
				}
				setData={setSimulation}
			/>

			<BatteryList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.battery || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as Battery[]
				}
				setData={setSimulation}
			/>

			<PvInstallationList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.pvinstallation || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as PvInstallation[]
				}
				setData={setSimulation}
			/>

			<HeatPumpSourceList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.heatpumpsource || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as HeatPumpSource[]
				}
				setData={setSimulation}
			/>

			<HeatPumpList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.heatpump || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as HeatPump[]
				}
				setData={setSimulation}
			/>

			<ElDemandList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.eldemand || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as ElDemand[]
				}
				setData={setSimulation}
			/>

			<HeatDemandList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.heatdemand || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as HeatDemand[]
				}
				setData={setSimulation}
			/>

			<PriceList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.price || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as Price[]
				}
				setData={setSimulation}
			/>

			<PriorityList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.priority || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as Priority[]
				}
				setData={setSimulation}
			/>
			<HardwareList
				disabled={simulation?.status?.running}
				data={
					Object.entries(simulation?.hardware || []).map((b) => {
						const name = b[0]
						const value = b[1] as object
						return Object.assign(value, { name: name } as object)
					}) as Hardware[]
				}
				setData={setSimulation}
			/>
		</Stack>
	)
}
